import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { THINKING_MESSAGES } from './thinking';
import type { Agent, ChatSession, Message, Model } from './types';

/**
 * All chat state and /api/chat/* calls. Moved verbatim from the former monolithic AgentChat
 * component; the UI components in this folder are purely presentational.
 */
export function useAgentChat() {
  const { user, getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingStage, setThinkingStage] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingModels, setLoadingModels] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);  // Mobile sidebar state

  // Cycle through thinking messages while loading
  useEffect(() => {
    if (!isLoading) {
      setThinkingStage(0);
      setElapsedTime(0);
      return;
    }

    // Store start time when loading begins
    const loadingStartTime = Date.now();

    // Update elapsed time every second
    const timeInterval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - loadingStartTime) / 1000));
    }, 1000);

    // Pick random message every 15 seconds
    const stageInterval = setInterval(() => {
      setThinkingStage(prev => {
        let next;
        do {
          next = Math.floor(Math.random() * THINKING_MESSAGES.length);
        } while (next === prev && THINKING_MESSAGES.length > 1);
        return next;
      });
    }, 15000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(stageInterval);
    };
  }, [isLoading]);

  useEffect(() => {
    loadAgents();
    loadModels();
    if (user) {
      loadChatSessions();
    }
  }, [user]);

  const loadChatSessions = async () => {
    if (!user) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/chat/sessions', { headers });
      const data = await response.json();
      setChatSessions(data.sessions || []);
    } catch (err) {
      console.error('Failed to load chat sessions:', err);
    }
  };

  const loadSession = async (sessionIdToLoad: string) => {
    if (!user) return;

    // Stop any existing polling for a different session
    pollingSessionRef.current = null;

    // Close sidebar on mobile when loading a session
    setSidebarOpen(false);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/chat/sessions/${sessionIdToLoad}`, { headers });
      const data = await response.json();
      if (data.session) {
        setCurrentSessionId(sessionIdToLoad);
        setSessionId(data.session.adk_session_id || null);
        const loadedMessages = data.session.messages || [];
        setMessages(loadedMessages);
        setSelectedAgent(data.session.agent_path);
        setSelectedModel(data.session.model);

        // Check if there's a pending message - if so, set loading state
        const hasPendingMessage = loadedMessages.some((m: Message) => m.pending);
        if (hasPendingMessage || data.session.has_pending) {
          setIsLoading(true);
          // Start polling for completion
          pollForCompletion(sessionIdToLoad);
        } else {
          // Make sure we're not showing loading for a completed session
          setIsLoading(false);
        }
      }
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  };

  // Poll for pending message completion
  // Uses a ref to track the session being polled so we don't overwrite other sessions
  const pollingSessionRef = useRef<string | null>(null);

  const pollForCompletion = async (sessionIdToPoll: string) => {
    const maxAttempts = 3600; // 60 minutes max (every 1 second)
    let attempts = 0;
    pollingSessionRef.current = sessionIdToPoll;

    const poll = async () => {
      // Stop polling if we switched to a different session
      if (pollingSessionRef.current !== sessionIdToPoll) {
        return;
      }

      if (attempts >= maxAttempts) {
        setIsLoading(false);
        return;
      }
      attempts++;

      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`/api/chat/sessions/${sessionIdToPoll}`, { headers });
        const data = await response.json();

        if (data.session) {
          const loadedMessages = data.session.messages || [];
          const stillPending = loadedMessages.some((m: Message) => m.pending) || data.session.has_pending;

          if (!stillPending) {
            // Done - response received, now update messages
            if (pollingSessionRef.current === sessionIdToPoll) {
              setMessages(loadedMessages);
              setIsLoading(false);
            }
            loadChatSessions(); // Refresh history
            return;
          }
          // Still pending - update messages to show progress updates
          // The pending message may have content (progress text) that we want to show
          if (pollingSessionRef.current === sessionIdToPoll) {
            setMessages(loadedMessages);
          }
        }

        // Still pending, poll again
        setTimeout(poll, 1000);
      } catch (err) {
        console.error('Poll error:', err);
        if (pollingSessionRef.current === sessionIdToPoll) {
          setIsLoading(false);
        }
      }
    };

    poll();
  };

  const deleteSession = async (sessionId: string) => {
    if (!user) return;
    try {
      const headers = await getAuthHeaders();
      await fetch(`/api/chat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers
      });
      if (currentSessionId === sessionId) {
        startNewChat();
      }
      loadChatSessions();
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const startNewChat = () => {
    pollingSessionRef.current = null;  // Stop any existing polling
    setMessages([]);
    setSessionId(null);
    setCurrentSessionId(null);
    setIsLoading(false);  // Stop any loading state from previous session
  };

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await fetch('/api/chat/agents');
      const data = await response.json();
      // Filter out agents with "eval" in their name or path (evaluation-only agents)
      const chatAgents = (data.agents || []).filter((agent: Agent) => {
        const nameLower = agent.name.toLowerCase();
        const pathLower = agent.path.toLowerCase();
        const displayLower = agent.display_name.toLowerCase();
        return !nameLower.includes('eval') && !pathLower.includes('eval') && !displayLower.includes('eval');
      });
      setAgents(chatAgents);
      // Select the latest added agent (last in the list) as default
      if (chatAgents.length > 0 && !selectedAgent) {
        setSelectedAgent(chatAgents[chatAgents.length - 1].path);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadModels = async () => {
    setLoadingModels(true);
    try {
      const response = await fetch('/api/chat/models');
      const data = await response.json();
      setModels(data.models || []);
      // Auto-select latest model: prefer gemini-3, then gemini-2.5, prefer pro over flash
      if (data.models && data.models.length > 0 && !selectedModel) {
        const modelList = data.models as Model[];
        // Priority: gemini-3-pro > gemini-3-flash > gemini-2.5-pro > gemini-2.5-flash > first
        const gemini3Pro = modelList.find((m) => m.name.includes('gemini-3') && m.name.includes('pro'));
        const gemini3Flash = modelList.find((m) => m.name.includes('gemini-3') && m.name.includes('flash'));
        const gemini25Pro = modelList.find((m) => m.name.includes('gemini-2.5') && m.name.includes('pro'));
        const gemini25Flash = modelList.find((m) => m.name.includes('gemini-2.5') && m.name.includes('flash'));
        const bestModel = gemini3Pro || gemini3Flash || gemini25Pro || gemini25Flash || modelList[0];
        setSelectedModel(bestModel.name);
      }
    } catch (err) {
      console.error('Failed to load models:', err);
    } finally {
      setLoadingModels(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !selectedAgent) return;

    const question = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    // Optimistically show user message with pending response
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: question,
    };
    const pendingMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: '',
      pending: true,
    };
    setMessages([...messages, userMessage, pendingMessage]);

    try {
      const headers = await getAuthHeaders();

      // Send to backend - it processes in background and returns immediately
      const response = await fetch('/api/chat/send', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: currentSessionId,
          question: question,
          agent_path: selectedAgent,
          model: selectedModel,
          adk_session_id: sessionId,
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Bericht versturen mislukt');
      }

      // Update session ID if new
      if (result.session_id && !currentSessionId) {
        setCurrentSessionId(result.session_id);
      }

      // Start polling for completion
      pollForCompletion(result.session_id);

    } catch (err: any) {
      // Show error immediately
      setMessages([...messages, userMessage, {
        id: pendingMessage.id,
        role: 'assistant',
        content: `Error: ${err.message}`,
        error: err.message,
        pending: false,
      }]);
      setIsLoading(false);
    }
  };

  // Reset session when agent or model changes (session is tied to agent+model)
  useEffect(() => {
    if (sessionId) {
      setSessionId(null);
    }
  }, [selectedAgent, selectedModel]);

  return {
    user,
    messages,
    inputValue,
    setInputValue,
    isLoading,
    thinkingStage,
    elapsedTime,
    agents,
    models,
    selectedAgent,
    setSelectedAgent,
    selectedModel,
    setSelectedModel,
    sessionId,
    chatSessions,
    currentSessionId,
    loadingAgents,
    loadingModels,
    sidebarOpen,
    setSidebarOpen,
    loadSession,
    deleteSession,
    startNewChat,
    sendMessage,
  };
}

export type AgentChatState = ReturnType<typeof useAgentChat>;
